/**
 * Shared cancellation helpers for long-running report-generation work.
 *
 * Abort reasons are preserved so the job runner can distinguish cancellation,
 * lost leases, and watchdog expiry without wrapping them as provider failures.
 */
export function abortReason(
  signal: AbortSignal | undefined,
  fallback = 'Operation cancelled',
): Error {
  const reason = signal?.reason;
  if (reason instanceof Error) return reason;
  const error = new Error(typeof reason === 'string' && reason ? reason : fallback);
  error.name = 'AbortError';
  return error;
}

export function throwIfAborted(signal?: AbortSignal, fallback?: string): void {
  if (signal?.aborted) throw abortReason(signal, fallback);
}

/** Re-throw the authoritative signal reason instead of degrading/retrying. */
export function rethrowIfAborted(signal: AbortSignal | undefined, _error?: unknown): void {
  if (signal?.aborted) throw abortReason(signal);
}

/** Delay that releases its timer and rejects immediately when cancelled. */
export function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  if (!signal) return new Promise(resolve => setTimeout(resolve, ms));

  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      reject(abortReason(signal));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** Race non-abort-aware work while ensuring its result cannot advance the job. */
export function raceWithAbort<T>(work: Promise<T>, signal?: AbortSignal): Promise<T> {
  throwIfAborted(signal);
  if (!signal) return work;

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(abortReason(signal));
    };
    signal.addEventListener('abort', onAbort, { once: true });
    work.then(
      value => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      error => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
}
